import type {UseFormReturn} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {useSearchParams} from 'react-router-dom';

import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormRequiredLegend,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

import type {AnyExportDefinition} from '../types/exportTypes';
import {EXPORT_FORMATS, exportFormatLabels} from '../utils/exportFormats';
import type {ExportFormValues} from '../utils/exportFormInputs';
import {EXPORT_PARAM} from '../utils/exportParams';

export type ExportFormFieldsProps = {
  form: UseFormReturn<ExportFormValues>;
  definitions: AnyExportDefinition[];
  definition?: AnyExportDefinition;
};

const ExportFormFields = ({
  form,
  definitions,
  definition,
}: ExportFormFieldsProps) => {
  const {t} = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const OptionsFields = definition?.OptionsFields;

  return (
    <>
      <FormRequiredLegend />
      <FormField
        control={form.control}
        name="list"
        render={({field}) => (
          <FormItem>
            <FormLabel required>{t('Data')}</FormLabel>
            <Select
              value={field.value}
              onValueChange={value => {
                searchParams.set(EXPORT_PARAM, value);
                setSearchParams(searchParams);
              }}
            >
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder={t('Select data')} />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {definitions.map(({id, label}) => (
                  <SelectItem key={id} value={id}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />
      {OptionsFields && <OptionsFields form={form} />}
      <FormField
        control={form.control}
        name="format"
        render={({field}) => (
          <FormItem>
            <FormLabel>{t('Format')}</FormLabel>
            <Select value={field.value} onValueChange={field.onChange}>
              <FormControl>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {EXPORT_FORMATS.map(format => (
                  <SelectItem key={format} value={format}>
                    {exportFormatLabels[format]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />
    </>
  );
};

export default ExportFormFields;
